import React from 'react';
import { Sparkles, Wand2, Loader2, Users, TrendingUp, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PersonaAnalysisData {
  summary: string;
  strengths: string[];
  areasOfImprovement: string[];
  recommendedNewPersonas: string[];
  demographicInsights?: string;
  brandPersonaAlignment?: string;
  gapAnalysis?: string;
}

interface PersonaAnalysisCardProps {
  title: string;
  analysisData?: PersonaAnalysisData;
  withAnalysis?: boolean;
  onAnalyze?: () => void;
  isAnalyzing?: boolean;
}

export const PersonaAnalysisCard: React.FC<PersonaAnalysisCardProps> = ({
  title,
  analysisData,
  withAnalysis = true,
  onAnalyze,
  isAnalyzing = false
}) => {
  const { t } = useTranslation();
  
  const hasCompleteData = Boolean(analysisData?.summary && analysisData?.strengths && analysisData?.areasOfImprovement);
  const shouldShowAnalysis = withAnalysis && hasCompleteData;
  
  const formatListItems = (items: string[] = []) => {
    return items.map((item, index) => (
      <li key={index} className="flex items-start">
        <span className="font-medium text-gray-900 mr-2">{index + 1}.</span>
        <span className="text-gray-700">{item}</span>
      </li>
    ));
  };
  
  return (
    <div className="rounded-xl border border-black shadow-lg bg-white overflow-hidden h-full">
      <div className="p-6 border-b border-gray-200" style={{ backgroundColor: "#F2F0FD" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900 font-title">
              {shouldShowAnalysis ? title : t('personas.analysis.aiReportTitle', 'AI Analysis Report')}
            </h2>
          </div>
        </div>
      </div>

      {shouldShowAnalysis ? (
        <div className="p-6" key="persona-analysis-content">
          <div className="flex flex-col space-y-6">
            {/* Summary Section */}
            <div>
              <h3 className="text-lg font-semibold text-indigo-900 mb-3 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                {t('personas.analysis.summary', 'Summary')}
              </h3>
              <p className="text-gray-700 leading-relaxed">{analysisData?.summary}</p>
            </div>
            
            {/* Two Column Layout for Strengths and Improvements */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Strengths */}
              <div className="border-2 border-green-100 rounded-lg p-4">
                <h3 className="text-sm font-medium text-green-600 mb-3 flex items-center">
                  <div className="w-1 h-4 bg-green-500 mr-2 rounded-full"></div>
                  <TrendingUp className="w-4 h-4 mr-1" />
                  {t('personas.analysis.strengths', 'Strengths')}
                </h3>
                <ul className="space-y-2 text-sm">
                  {formatListItems(analysisData?.strengths)}
                </ul>
              </div>
              
              {/* Areas of Improvement */}
              <div className="border-2 border-orange-100 rounded-lg p-4">
                <h3 className="text-sm font-medium text-orange-600 mb-3 flex items-center">
                  <div className="w-1 h-4 bg-orange-500 mr-2 rounded-full"></div>
                  <Lightbulb className="w-4 h-4 mr-1" />
                  {t('personas.analysis.improvements', 'Areas for Improvement')}
                </h3>
                <ul className="space-y-2 text-sm">
                  {formatListItems(analysisData?.areasOfImprovement)}
                </ul>
              </div>
            </div>

            {/* Advanced Insights Grid Layout */}
            {((analysisData?.recommendedNewPersonas && analysisData.recommendedNewPersonas.length > 0) || 
              (analysisData?.demographicInsights || analysisData?.brandPersonaAlignment || analysisData?.gapAnalysis)) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Recommended New Personas */}
                {analysisData?.recommendedNewPersonas && analysisData.recommendedNewPersonas.length > 0 && (
                  <div className="border-2 border-blue-100 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-600 mb-3 flex items-center">
                      <div className="w-1 h-4 bg-blue-500 mr-2 rounded-full"></div>
                      <Users className="w-4 h-4 mr-1" />
                      {t('personas.analysis.recommendedPersonas', 'Recommended New Personas')}
                    </h3>
                    <ul className="space-y-2 text-sm">
                      {formatListItems(analysisData.recommendedNewPersonas)}
                    </ul>
                  </div>
                )}

                {/* Experts Insights */}
                {(analysisData?.demographicInsights || analysisData?.brandPersonaAlignment || analysisData?.gapAnalysis) && (
                  <div className="border-2 border-purple-100 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-purple-600 mb-3 flex items-center">
                      <div className="w-1 h-4 bg-purple-500 mr-2 rounded-full"></div>
                      <Lightbulb className="w-4 h-4 mr-1" />
                      {t('personas.analysis.expertsInsights', 'Experts Insights')}
                    </h3>
                    <div className="space-y-3 text-sm">
                      {analysisData.demographicInsights && (
                        <div>
                          <h4 className="font-medium text-purple-700 mb-1">
                            • {t('personas.analysis.demographicInsights', 'Demographic Insights')}
                          </h4>
                          <p className="text-gray-700 ml-4">{analysisData.demographicInsights}</p>
                        </div>
                      )}
                      {analysisData.brandPersonaAlignment && (
                        <div>
                          <h4 className="font-medium text-purple-700 mb-1">
                            • {t('personas.analysis.brandPersonaAlignment', 'Brand-Persona Alignment')}
                          </h4>
                          <p className="text-gray-700 ml-4">{analysisData.brandPersonaAlignment}</p>
                        </div>
                      )}
                      {analysisData.gapAnalysis && (
                        <div>
                          <h4 className="font-medium text-purple-700 mb-1">
                            • {t('personas.analysis.gapAnalysis', 'Gap Analysis')}
                          </h4>
                          <p className="text-gray-700 ml-4">{analysisData.gapAnalysis}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-6" key="persona-analysis-cta-content">
          <div className="flex flex-col items-center justify-center text-center py-8">
            <Wand2 className="w-12 h-12 text-indigo-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('common.ai.getAnalysisTitleGeneral', 'Get AI Analysis')} 
            </h3>
            <p className="text-gray-600 mb-6 max-w-md">
              {t('personas.analysis.description', 'Get AI insights about your current personas and recommendations for new ones to better understand your target audience.')}
            </p>
            <button
              onClick={onAnalyze}
              disabled={isAnalyzing || !onAnalyze}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-indigo-400 disabled:cursor-not-allowed"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('common.ai.analyzing', 'Analyzing...')}
                </>
              ) : (
                <>
                  <Wand2 className="w-5 h-5 mr-2" />
                  {t('common.ai.analyzeButton', 'Analyze Now')}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 